import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import CustomerManagement from './components/CustomerManagement';
import ProductManagement from './components/ProductManagement';
import UserManagement from './components/UserManagement';
import MachineManagement from './components/MachineManagement';
import ImportOrders from './components/ImportOrders';
import OPCUAConfig from './components/OPCUAConfig';
import EdgeGatewayManagement from './components/EdgeGatewayManagement';
import GatewayGuide from './components/GatewayGuide';
import MachineDetail from './components/MachineDetail';
import OrderManagement from './components/OrderManagement';
import OrderDetail from './components/OrderDetail';
import Layout from './components/Layout';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

// Tema Material-UI - Bionen brand style
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#32373c',
      light: '#545a60',
      dark: '#1e2226',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#0693e3',
      light: '#42b0f5',
      dark: '#0570ad',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f5f6f8',
      paper: '#ffffff',
    },
    text: {
      primary: '#32373c',
      secondary: '#6b7280',
    },
    divider: '#e5e7eb',
    success: {
      main: '#10b981',
    },
    warning: {
      main: '#f59e0b',
    },
    error: {
      main: '#ef4444',
    },
  },
  typography: {
    fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
    fontSize: 14,
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          minHeight: '44px',
          borderRadius: '9999px',
          fontWeight: 500,
          padding: '8px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: '#1e2226',
          },
        },
        containedSecondary: {
          '&:hover': {
            backgroundColor: '#0570ad',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '10px 16px',
          borderColor: '#e5e7eb',
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#f9fafb',
          color: '#32373c',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
        },
        elevation3: {
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(6, 147, 227, 0.1)',
            color: '#0693e3',
            '& .MuiListItemIcon-root': {
              color: '#0693e3',
            },
            '&:hover': {
              backgroundColor: 'rgba(6, 147, 227, 0.15)',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
          },
        },
      },
    },
  },
});

// Componente per proteggere le route
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return isAuthenticated() ? children : <Navigate to="/login" replace />;
};

// Componente per le route admin
const AdminRoute = ({ children }) => {
  const { isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return isAdmin() ? children : <Navigate to="/dashboard" replace />;
};

// Componente principale per le route
const AppRoutes = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          isAuthenticated() ? <Navigate to="/dashboard" replace /> : <Login />
        } 
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <Layout>
              <OrderManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <OrderDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
            <Layout>
              <CustomerManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            <Layout>
              <ProductManagement />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <AdminRoute>
            <Layout>
              <UserManagement />
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/machines"
        element={
          <AdminRoute>
            <Layout>
              <MachineManagement />
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/machines/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <MachineDetail />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/import"
        element={
          <AdminRoute>
            <Layout>
              <ImportOrders />
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/opcua-config"
        element={
          <AdminRoute>
            <Layout>
              <OPCUAConfig />
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/edge-gateways"
        element={
          <AdminRoute>
            <Layout>
              <EdgeGatewayManagement />
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/gateway-guide"
        element={
          <AdminRoute>
            <Layout>
              <GatewayGuide />
            </Layout>
          </AdminRoute>
        }
      />
      <Route
        path="/"
        element={<Navigate to="/dashboard" replace />}
      />
      <Route 
        path="*" 
        element={<Navigate to="/dashboard" replace />} 
      />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;